/**
 * Frontend entry point.
 *
 * Routing: History API, clean URLs — /login, /register, /forgot-password,
 * /reset-password?token=..., /verify-email?token=..., /play (authenticated).
 *
 * Game developers wire their rendering engine inside mountGame() — replace
 * the placeholder with PixiJS, Babylon.js, or DOM rendering.
 */
import { bootstrapSession, logout } from './core/auth/auth-api';
import { renderLogin } from './core/auth/login-screen';
import { renderRegister } from './engine/register-screen';
import { renderForgotPassword } from './core/auth/forgot-password-screen';
import { renderResetPassword } from './core/auth/reset-password-screen';
import { renderVerifyEmail } from './core/auth/verify-email-screen';
import { renderCheckEmail } from './core/auth/check-email-screen';
import { authStore } from './core/state/auth-store';
import { connectSocket, disconnectSocket } from './core/network/socket';
import { fetchSiteInfo, isPreviewBuild, stripBrandMeta } from './core/preview/preview';
import { mountGame, unmountGame } from './engine/bootstrap';
import { renderLandingScreen } from './engine/landing/landing-screen';
import { renderNewsList, renderNewsArticle } from './engine/landing/news-screen';
import { renderPaymentResult } from './screens/payment-result-screen';
import { enhanceAuthScreen, destroyAuthShell } from './engine/auth-wrapper';
import { applyReduceMotion, isReduceMotionEnabled } from './engine/reduce-motion';

// Applied before anything else so there's no flash of motion on a reload —
// see engine/reduce-motion.ts (the Settings screen applies the same call on
// its own mount, in case that ever runs without main.ts, e.g. in tests).
applyReduceMotion(isReduceMotionEnabled());

const app = document.getElementById('app')!;
let teardown: (() => void) | null = null;

export function navigate(to: string) {
  if (location.pathname !== to) history.pushState(null, '', to);
  render();
}

const AUTH_PATHS = new Set(['/login', '/register', '/forgot-password', '/reset-password', '/verify-email', '/check-email']);

function render() {
  teardown?.();
  teardown = null;

  const path = location.pathname;
  const params = new URLSearchParams(location.search);
  const user = authStore.get().user;

  if (!AUTH_PATHS.has(path)) destroyAuthShell();

  const afterAuth = () => {
    const u = authStore.get().user;
    if (u && !u.emailVerified) { navigate('/check-email'); return; }
    connectSocket();
    navigate('/play');
  };

  switch (path) {
    case '/login': {
      if (user) { navigate(user.emailVerified ? '/play' : '/check-email'); return; }
      const coreLogin = renderLogin(app, afterAuth);
      const authLogin = enhanceAuthScreen(app);
      teardown = () => { authLogin(); coreLogin(); };
      break;
    }
    case '/register': {
      if (user) { navigate(user.emailVerified ? '/play' : '/check-email'); return; }
      const coreReg = renderRegister(app, afterAuth);
      const authReg = enhanceAuthScreen(app);
      teardown = () => { authReg(); coreReg(); };
      break;
    }
    case '/forgot-password': {
      if (user) { navigate('/play'); return; }
      const coreForgot = renderForgotPassword(app);
      const authForgot = enhanceAuthScreen(app);
      teardown = () => { authForgot(); coreForgot(); };
      break;
    }
    case '/reset-password': {
      const coreReset = renderResetPassword(app, params.get('token') ?? '', () => navigate('/login'));
      const authReset = enhanceAuthScreen(app);
      teardown = () => { authReset(); coreReset(); };
      break;
    }
    case '/verify-email': {
      const coreVerify = renderVerifyEmail(app, params.get('token') ?? '');
      const authVerify = enhanceAuthScreen(app);
      teardown = () => { authVerify(); coreVerify(); };
      break;
    }
    case '/check-email': {
      if (!user) { navigate('/login'); return; }
      const coreCheck = renderCheckEmail(app, user.email, () => { disconnectSocket(); navigate('/login'); });
      const authCheck = enhanceAuthScreen(app);
      teardown = () => { authCheck(); coreCheck(); };
      break;
    }
    case '/play':
    case '/':
      if (!user) {
        if (path === '/') { teardown = renderLandingScreen(app, () => navigate('/login')); break; }
        navigate('/login');
        return;
      }
      if (!user.emailVerified) { navigate('/check-email'); return; }
      connectSocket();
      teardown = mountGame(app, user, () => { logout(); disconnectSocket(); navigate('/login'); });
      break;
    case '/payment-result':
      // Gateway return. The order id survives a login detour in sessionStorage
      // (see core/payments/deposit-flow.ts), so a lapsed session loses nothing.
      if (!user) { navigate('/login'); return; }
      teardown = renderPaymentResult(app, () => navigate('/play'));
      break;
    case '/news':
      teardown = renderNewsList(app, navigate);
      break;
    default:
      if (path.startsWith('/news/')) {
        const slug = path.slice(6);
        teardown = renderNewsArticle(app, slug, navigate);
        break;
      }
      navigate('/login');
  }
}

window.addEventListener('popstate', render);

document.addEventListener('click', (e) => {
  // An ancestor that already handled this click (e.g. the landing page's own
  // scroll-to-section handler) calls preventDefault() and does its own thing
  // — without this check, the click kept bubbling here, which resolved a
  // fragment-only href like "#how" against the CURRENT path and called
  // navigate('/'), tearing down and resynchronously remounting the whole
  // screen mid-animation. That is why clicking the landing page's nav links
  // appeared to do nothing: the scroll was scheduled, then the entire DOM it
  // was scrolling was replaced before the browser could paint it.
  if (e.defaultPrevented) return;
  const a = (e.target as HTMLElement).closest('a[href]') as HTMLAnchorElement | null;
  if (!a || a.target || a.origin !== location.origin) return;
  e.preventDefault();
  navigate(a.pathname + a.search);
});

(async () => {
  // Fetch site-info first — drives preview-mode gating throughout the app
  await fetchSiteInfo();
  if (isPreviewBuild()) stripBrandMeta();

  await bootstrapSession();
  render();

  authStore.subscribe((s) => {
    if (!s.user && location.pathname === '/play') navigate('/login');
  });
})();

window.addEventListener('beforeunload', () => {
  teardown?.();
  unmountGame();
});
